/**
 * Unit conversion system for SenCore.
 * Data is always stored in SI/standard units in the database.
 * Conversion happens only at display time in the frontend.
 */

export interface UnitOption {
  unit: string;
  label: string;
  convert: (value: number) => number;
}

// Available unit options per metric (first one is always the default/SI unit)
export const UNIT_OPTIONS: Record<string, UnitOption[]> = {
  // Temperature metrics
  temperature_celsius: [
    { unit: '°C', label: 'Celsius', convert: (v) => v },
    { unit: '°F', label: 'Fahrenheit', convert: (v) => v * 9 / 5 + 32 },
    { unit: 'K', label: 'Kelvin', convert: (v) => v + 273.15 },
  ],
  dew_point_celsius: [
    { unit: '°C', label: 'Celsius', convert: (v) => v },
    { unit: '°F', label: 'Fahrenheit', convert: (v) => v * 9 / 5 + 32 },
  ],
  soil_temperature_10cm_celsius: [
    { unit: '°C', label: 'Celsius', convert: (v) => v },
    { unit: '°F', label: 'Fahrenheit', convert: (v) => v * 9 / 5 + 32 },
  ],
  soil_temperature_20cm_celsius: [
    { unit: '°C', label: 'Celsius', convert: (v) => v },
    { unit: '°F', label: 'Fahrenheit', convert: (v) => v * 9 / 5 + 32 },
  ],
  soil_temperature_30cm_celsius: [
    { unit: '°C', label: 'Celsius', convert: (v) => v },
    { unit: '°F', label: 'Fahrenheit', convert: (v) => v * 9 / 5 + 32 },
  ],
  soil_temperature_50cm_celsius: [
    { unit: '°C', label: 'Celsius', convert: (v) => v },
    { unit: '°F', label: 'Fahrenheit', convert: (v) => v * 9 / 5 + 32 },
  ],
  soil_temperature_100cm_celsius: [
    { unit: '°C', label: 'Celsius', convert: (v) => v },
    { unit: '°F', label: 'Fahrenheit', convert: (v) => v * 9 / 5 + 32 },
  ],
  leaf_temperature_celsius: [
    { unit: '°C', label: 'Celsius', convert: (v) => v },
    { unit: '°F', label: 'Fahrenheit', convert: (v) => v * 9 / 5 + 32 },
  ],
  water_temperature_celsius: [
    { unit: '°C', label: 'Celsius', convert: (v) => v },
    { unit: '°F', label: 'Fahrenheit', convert: (v) => v * 9 / 5 + 32 },
  ],
  road_surface_temp_celsius: [
    { unit: '°C', label: 'Celsius', convert: (v) => v },
    { unit: '°F', label: 'Fahrenheit', convert: (v) => v * 9 / 5 + 32 },
  ],

  // Wind speed
  wind_speed_m_per_s: [
    { unit: 'm/s', label: 'Meters/sec', convert: (v) => v },
    { unit: 'km/h', label: 'Kilometers/hour', convert: (v) => v * 3.6 },
    { unit: 'mph', label: 'Miles/hour', convert: (v) => v * 2.23694 },
    { unit: 'knots', label: 'Knots', convert: (v) => v * 1.94384 },
  ],
  wind_gust_m_per_s: [
    { unit: 'm/s', label: 'Meters/sec', convert: (v) => v },
    { unit: 'km/h', label: 'Kilometers/hour', convert: (v) => v * 3.6 },
    { unit: 'mph', label: 'Miles/hour', convert: (v) => v * 2.23694 },
    { unit: 'knots', label: 'Knots', convert: (v) => v * 1.94384 },
  ],
  vehicle_speed_avg_kmh: [
    { unit: 'km/h', label: 'Kilometers/hour', convert: (v) => v },
    { unit: 'mph', label: 'Miles/hour', convert: (v) => v * 0.62137 },
  ],

  // Pressure
  atmospheric_pressure_mbar: [
    { unit: 'mbar', label: 'Millibar', convert: (v) => v },
    { unit: 'hPa', label: 'Hectopascal', convert: (v) => v },
    { unit: 'mmHg', label: 'mmHg', convert: (v) => v * 0.75006 },
    { unit: 'inHg', label: 'inHg', convert: (v) => v * 0.02953 },
    { unit: 'atm', label: 'Atmosphere', convert: (v) => v * 0.000986923 },
  ],

  // Rainfall / ET
  rain_mm: [
    { unit: 'mm', label: 'Millimeters', convert: (v) => v },
    { unit: 'in', label: 'Inches', convert: (v) => v * 0.03937 },
  ],
  reference_et_mm: [
    { unit: 'mm', label: 'Millimeters', convert: (v) => v },
    { unit: 'in', label: 'Inches', convert: (v) => v * 0.03937 },
  ],

  // Visibility
  visibility_km: [
    { unit: 'km', label: 'Kilometers', convert: (v) => v },
    { unit: 'mi', label: 'Miles', convert: (v) => v * 0.62137 },
    { unit: 'm', label: 'Meters', convert: (v) => v * 1000 },
  ],

  // Water level
  water_level_m: [
    { unit: 'm', label: 'Meters', convert: (v) => v },
    { unit: 'ft', label: 'Feet', convert: (v) => v * 3.28084 },
    { unit: 'cm', label: 'Centimeters', convert: (v) => v * 100 },
  ],

  // Water flow
  water_flow_m3s: [
    { unit: 'm³/s', label: 'Cubic meters/sec', convert: (v) => v },
    { unit: 'L/s', label: 'Liters/sec', convert: (v) => v * 1000 },
    { unit: 'ft³/s', label: 'Cubic feet/sec', convert: (v) => v * 35.3147 },
  ],

  // Solar radiation
  solar_radiation_watt_per_sqm: [
    { unit: 'W/m²', label: 'Watts/m²', convert: (v) => v },
    { unit: 'MJ/m²/day', label: 'MJ/m²/day', convert: (v) => v * 0.0864 },
  ],
};

/**
 * Get available unit options for a metric.
 * Returns null if the metric has no alternative units.
 */
export const getUnitOptions = (metricField: string): UnitOption[] | null => {
  return UNIT_OPTIONS[metricField] || null;
};

/**
 * Get the default (SI) unit for a metric.
 */
export const getDefaultUnit = (metricField: string): string => {
  const options = UNIT_OPTIONS[metricField];
  return options ? options[0].unit : '';
};

/**
 * Convert a value from SI to the preferred unit.
 */
export const convertValue = (
  value: number,
  metricField: string,
  preferredUnit: string | undefined
): number => {
  if (!preferredUnit) return value;
  const options = UNIT_OPTIONS[metricField];
  if (!options) return value;
  const option = options.find(o => o.unit === preferredUnit);
  return option ? option.convert(value) : value;
};

/**
 * Get the display unit for a metric given station preferences.
 */
export const getDisplayUnit = (
  metricField: string,
  unitPreferences: Record<string, string> | null | undefined
): string => {
  if (unitPreferences && unitPreferences[metricField]) {
    return unitPreferences[metricField];
  }
  return getDefaultUnit(metricField);
};

/**
 * Convert and format a reading value for display.
 */
export const formatConvertedValue = (
  value: number,
  metricField: string,
  unitPreferences: Record<string, string> | null | undefined,
  decimals: number = 2
): { displayValue: string; displayUnit: string } => {
  const displayUnit = getDisplayUnit(metricField, unitPreferences);
  const converted = convertValue(value, metricField, displayUnit);
  return {
    displayValue: converted.toFixed(decimals),
    displayUnit,
  };
};

/**
 * Get all metrics that have convertible units.
 */
export const getConvertibleMetrics = (): string[] => {
  return Object.keys(UNIT_OPTIONS);
};
